import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { groupBy, sum, uniq, uniqBy } from "lodash-es";
import { createContext, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import z from "zod";

import { cartSchema } from "@/components/registrations/schemas";
import { getViewerProfilesQueryOptions } from "@/functions/profiles/get-viewer-profiles";
import { useTRPC } from "@/integrations/trpc/react";
import { getTournamentDivisionsQueryOptions } from "@/lib/tournament-divisions/get-tournament-divisions";
import { isDefined } from "@/utils/types";

// Context for tracking the currently dragged profile
type DragContextValue = {
  draggedProfile: CartProfile | null;
  setDraggedProfile: (profile: CartProfile | null) => void;
};

export const DragContext = createContext<DragContextValue | null>(null);

export function useDraggedProfile() {
  const context = useContext(DragContext);
  return context?.draggedProfile ?? null;
}

export function useSetDraggedProfile() {
  const context = useContext(DragContext);
  return context?.setDraggedProfile ?? (() => {});
}

export const registrationPageSchema = cartSchema.extend({
  profiles: z.array(z.number()).default([]),
  teams: z
    .array(
      z.object({
        id: z.uuidv4().default(uuidv4()),
        divisionId: z.number(),
        profileIds: z.array(z.number()),
      }),
    )
    .default([]),
});

type CartRoute = "registration" | "checkout" | "success";

function getRouteFrom(route?: CartRoute) {
  switch (route) {
    case "checkout":
      return "/account/registrations/checkout";
    case "success":
      return "/account/registrations/success";
    default:
      return "/account/registrations/";
  }
}

export function useCartProfiles(route?: CartRoute) {
  const trpc = useTRPC();
  const {
    profiles: profileIds,
    teams,
    memberships,
  } = useSearch({
    from: getRouteFrom(route),
  });

  // Also fetch profiles referenced in teams and memberships so that
  // past teammates (or any profile added to a team/membership) are resolved
  const teamProfileIds = teams.flatMap((t) => t.profileIds);
  const membershipProfileIds = memberships.map((m) => m.profileId);
  const allProfileIds = uniq([
    ...profileIds,
    ...teamProfileIds,
    ...membershipProfileIds,
  ]);

  const { data: viewerProfiles = [] } = useQuery({
    ...getViewerProfilesQueryOptions(),
    select: (data) => {
      return data.map((profile) => ({
        ...profile,
        registrations: 0,
      }));
    },
  });

  const { data: otherProfiles = [] } = useQuery({
    ...trpc.profiles.listByIds.queryOptions({
      ids: allProfileIds,
    }),
    enabled: allProfileIds.length > 0,
    select: (data) => {
      return data.map((profile) => ({
        ...profile,
        registrations: 0,
      }));
    },
  });

  return uniqBy(viewerProfiles.concat(otherProfiles), "id");
}

export type CartProfile = ReturnType<typeof useCartProfiles>[number];

function useYourProfiles(route?: CartRoute) {
  const cartProfiles = useCartProfiles(route);
  const { profiles: explicitProfileIds } = useSearch({
    from: getRouteFrom(route),
  });
  const { data: viewerProfileIds = [] } = useQuery({
    ...getViewerProfilesQueryOptions(),
    select: (data) => data.map((p) => p.id),
  });

  return cartProfiles.filter(
    (p) => viewerProfileIds.includes(p.id) || explicitProfileIds.includes(p.id),
  );
}

function usePastTeammatesQuery() {
  const trpc = useTRPC();

  return useInfiniteQuery(
    trpc.profiles.getPastTeammates.infiniteQueryOptions(
      { cursor: null },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      },
    ),
  );
}

// ── All Profiles Context ──
// Computed once in RegistrationPageContent, consumed by all children.

type AllProfilesContextValue = {
  yourProfiles: CartProfile[];
  pastTeammates: CartProfile[];
  /** Cart profiles merged with past teammates — the full set for drop targets */
  allProfiles: CartProfile[];
  pastTeammatesQuery: ReturnType<typeof usePastTeammatesQuery>;
};

export const AllProfilesContext = createContext<AllProfilesContextValue | null>(
  null,
);

/** Call once at the top of the registration page to compute the shared value. */
export function useAllProfilesProvider(): AllProfilesContextValue {
  const yourProfiles = useYourProfiles();
  const cartProfiles = useCartProfiles();
  const query = usePastTeammatesQuery();

  const yourProfileIds = yourProfiles.map((p) => p.id);
  const pastTeammates =
    query.data?.pages
      .flatMap((page) => page.data)
      .filter((p) => !yourProfileIds.includes(p.id))
      .map(
        (p) =>
          ({ ...p, registrations: 0 }) satisfies Pick<
            CartProfile,
            "registrations"
          > as CartProfile,
      ) ?? [];

  const allProfiles = uniqBy([...cartProfiles, ...pastTeammates], "id");

  return {
    yourProfiles,
    pastTeammates,
    allProfiles,
    pastTeammatesQuery: query,
  };
}

/** Consume the shared profiles context in any child component. */
export function useAllProfiles() {
  const ctx = useContext(AllProfilesContext);
  if (!ctx) {
    throw new Error(
      "useAllProfiles must be used within an AllProfilesContext.Provider",
    );
  }
  return ctx;
}

export function useCartDivisionRegistrations(route?: CartRoute) {
  const { teams } = useSearch({
    from: getRouteFrom(route),
  });

  const divisions = groupBy(teams, "divisionId");

  return Object.entries(divisions).map(([divisionId, teams]) => ({
    divisionId: Number.parseInt(divisionId, 10),
    teams,
  }));
}

export function useCartDivisions(route?: CartRoute) {
  const registrations = useCartDivisionRegistrations(route);
  const divisionIds = registrations.map((r) => r.divisionId);

  const { data = [] } = useQuery({
    ...getTournamentDivisionsQueryOptions(divisionIds),
    select: (data) =>
      data.map((div) => ({
        ...div,
        teams:
          registrations.find((reg) => reg.divisionId === div.id)?.teams ?? [],
      })),
  });

  return data;
}

export type CartDivision = ReturnType<typeof useCartDivisions>[number];

export function useCart(route?: CartRoute) {
  const data = useSearch({
    from: getRouteFrom(route),
  });

  return data;
}

export function useMembershipPrice() {
  const trpc = useTRPC();
  const { data = null } = useQuery({
    ...trpc.settings.get.queryOptions({ key: "membership-price" }),
    select: (setting) => {
      if (typeof setting?.value === "number" && setting.value > 0)
        return setting.value;
      return null;
    },
  });

  return data;
}

export function useMembershipsAvailable() {
  const price = useMembershipPrice();
  return price !== null;
}

const defaultRegistrationSettings = {
  registrationOpen: false,
  defaultPrice: null,
  defaultOpenPrice: null,
} as const;

export function useRegistrationSettings() {
  const trpc = useTRPC();
  const { data = defaultRegistrationSettings } = useQuery(
    trpc.settings.getRegistrationSettings.queryOptions(),
  );
  return data;
}

export function useDefaultTournamentPrice() {
  return useRegistrationSettings().defaultPrice;
}

export function useDefaultOpenTournamentPrice() {
  return useRegistrationSettings().defaultOpenPrice;
}

export function useIsSeasonOpen() {
  return useRegistrationSettings().registrationOpen;
}

/**
 * Determines if registration is open for a tournament division.
 *
 * Registration is open if:
 * - The division is not paused (registrationPaused is false)
 * - The season is open (tournament-registration-open setting is true)
 * - A price is available (either division-specific or appropriate default)
 * - AND either no registration open time is set, or now >= registrationOpenAt
 */
export function useIsRegistrationOpen({
  registrationPrice,
  registrationOpenAt,
  registrationPaused,
  divisionName,
}: {
  registrationPrice: number | null;
  registrationOpenAt: Date | null;
  registrationPaused?: boolean;
  divisionName?: string;
}) {
  const { registrationOpen, defaultPrice, defaultOpenPrice } =
    useRegistrationSettings();

  if (registrationPaused) {
    return false;
  }

  if (!registrationOpen) {
    return false;
  }

  // Pick the correct default price based on division type
  const appropriateDefaultPrice =
    divisionName === "open" ? defaultOpenPrice : defaultPrice;

  // Check if a price is available (division-specific or default)
  const hasPrice =
    (registrationPrice !== null && Number(registrationPrice) > 0) ||
    appropriateDefaultPrice !== null;

  if (!hasPrice) {
    return false;
  }

  // If registrationOpenAt (timestamp) is set, use it for precise comparison
  if (registrationOpenAt) {
    return new Date() >= registrationOpenAt;
  }

  // If neither is set, registration is open
  return true;
}

export function useMembershipItems(route?: CartRoute) {
  const { memberships } = useSearch({
    from: getRouteFrom(route),
  });

  const profiles = useCartProfiles(route);
  const membershipPrice = useMembershipPrice();

  if (membershipPrice === null) {
    return [];
  }

  return memberships
    .map((item) => ({
      profile: profiles.find((p) => p.id === item.profileId),
      tshirtSize: item.tshirtSize,
    }))
    .filter(({ profile }) => isDefined(profile))
    .map(({ profile, tshirtSize }) => ({
      type: "membership" as const,
      title: "Annual Membership",
      price: membershipPrice,
      profiles: [profile!],
      tshirtSize,
    }));
}

export function useTeamItems(route?: CartRoute) {
  const divisions = useCartDivisions(route);
  const profiles = useCartProfiles(route);
  const { registrationOpen, defaultPrice, defaultOpenPrice } =
    useRegistrationSettings();

  if (!registrationOpen) {
    return [];
  }

  return divisions.flatMap((division) => {
    const isOpen = division.division.name === "open";
    const appropriateDefaultPrice = isOpen ? defaultOpenPrice : defaultPrice;
    const price = division.registrationPrice ?? appropriateDefaultPrice;

    // No price available means registration isn't open for this division
    if (price == null) {
      return [];
    }

    const spotsAvailable =
      division.waitlistedCount > 0
        ? 0
        : Math.max(0, division.capacity - division.confirmedCount);
    const isWaitlistFull =
      division.confirmedCount + division.waitlistedCount >=
      division.capacity + division.waitlistCapacity;

    // Filter out teams with no players - they're still being set up
    const teamsWithPlayers = division.teams.filter(
      (t) => t.profileIds.length > 0,
    );

    return teamsWithPlayers.map((team, index) => {
      const teamProfiles = team.profileIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter(isDefined);

      return {
        type: "team" as const,
        title: `Team ${index + 1}`,
        division,
        price,
        profiles: teamProfiles,
        isWaitlisted: !isWaitlistFull && index >= spotsAvailable,
      };
    });
  });
}

export function useCartItems(route?: CartRoute) {
  const membershipItems = useMembershipItems(route);
  const teamItems = useTeamItems(route);

  return [...membershipItems, ...teamItems];
}

export function useCartTotal(route?: CartRoute) {
  const items = useCartItems(route);

  return sum(items.map(({ price }) => price));
}

export function useCartValidation(route?: CartRoute) {
  const trpc = useTRPC();
  const { memberships, teams } = useSearch({
    from: getRouteFrom(route),
  });

  // Transform teams to match cartSchema (remove the id field)
  // Filter out teams with no players - they're still being set up
  const cartTeams = teams
    .filter((t) => t.profileIds.length > 0)
    .map(({ divisionId, profileIds }) => ({
      divisionId,
      profileIds,
    }));

  const { data, isLoading, error } = useQuery({
    ...trpc.payments.validateCart.queryOptions({
      memberships,
      teams: cartTeams,
    }),
    // Only run validation when there's something in the cart
    enabled: memberships.length > 0 || teams.length > 0,
  });

  return {
    isValid: data?.valid ?? false,
    errors: data?.errors ?? [],
    isLoading,
    error,
  };
}
